/**
 * Shared helpers for the staff store modules (Zustand).
 *
 * Requests authenticate with a bearer token (`Authorization: Bearer …`) and are
 * always sent WITHOUT credentials: the API's CORS policy does not allow
 * credentialed requests, so `credentials: "include"` would be blocked by the
 * browser as a CORS error.
 */
import { API_URL } from "../../constants";
import { toDisplayString } from "../../utils/toDisplayString";
import authSession from "../../lib/authSession";

export { API_URL };

/** Standard JSON headers; attaches the bearer token when present. */
export function authHeaders(token) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** Resolve the current access token from the store, then the persisted session. */
export function resolveToken(get) {
  const state = get();
  return state.staffToken || authSession.getToken() || "";
}

/**
 * Authenticated JSON request that unwraps the API error envelope
 * (`{ error: { message } }`) into a thrown Error with a displayable message.
 */
export async function apiRequest(get, path, { method = "GET", body, token, credentials } = {}) {
  const bearer = token !== undefined ? token : resolveToken(get);
  const response = await fetch(`${API_URL}${path}`, {
    method,
    // Auth endpoints pass credentials: "include"; data endpoints authenticate
    // with the Bearer token (the API calls default to credentials: "omit").
    ...(credentials ? { credentials } : {}),
    headers: authHeaders(bearer),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const looksJson =
      contentType.includes("application/json") ||
      text.trim().startsWith("{") ||
      text.trim().startsWith("[");
    if (looksJson) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    throw new Error(
      toDisplayString(data?.error ?? data?.message, `Request failed (${response.status})`)
    );
  }

  return data;
}

/**
 * Normalize the many list shapes an endpoint may return into
 * `{ items, total, totalPages }`. Pass alternative item keys via `keys`.
 */
export function normalizeList(data, keys = []) {
  if (!data || typeof data !== "object") return { items: [], total: 0, totalPages: 1 };

  let items = [];
  if (Array.isArray(data)) {
    items = data;
  } else if (Array.isArray(data.data)) {
    items = data.data;
  } else {
    const payload = data.data && typeof data.data === "object" ? data.data : data;
    items = payload.items || data.items || [];
    if (!items.length) {
      for (const key of keys) {
        if (Array.isArray(payload[key])) {
          items = payload[key];
          break;
        }
        if (Array.isArray(data[key])) {
          items = data[key];
          break;
        }
      }
    }
  }

  const pagination = data.pagination || {};
  return {
    items: Array.isArray(items) ? items : [],
    total: pagination.total ?? data.total ?? (Array.isArray(items) ? items.length : 0),
    totalPages:
      pagination.totalPages ?? pagination.pages ?? data.totalPages ?? data.pages ?? 1,
  };
}

/** Recursively find a token string in a login/refresh response. */
export function findToken(node, visited = new Set()) {
  if (node === null || typeof node !== "object" || visited.has(node)) return undefined;
  visited.add(node);
  for (const key of ["token", "accessToken", "access_token", "authToken", "jwt"]) {
    if (typeof node[key] === "string" && node[key].length > 0) return node[key];
  }
  for (const key of ["data", "result", "staff", "auth", "user", "payload"]) {
    const nested = node[key];
    if (nested && typeof nested === "object") {
      const found = findToken(nested, visited);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

/** Recursively find a refresh token string in a login/refresh response. */
export function findRefreshToken(node, visited = new Set()) {
  if (node === null || typeof node !== "object" || visited.has(node)) return undefined;
  visited.add(node);
  for (const key of ["refreshToken", "refresh_token"]) {
    if (typeof node[key] === "string" && node[key].length > 0) return node[key];
  }
  for (const key of ["data", "result", "staff", "auth", "user", "payload"]) {
    const nested = node[key];
    if (nested && typeof nested === "object") {
      const found = findRefreshToken(nested, visited);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}
